import React, { useState, useEffect } from 'react'; // Added useEffect
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
// Removed AuthContext import

const CompanySignup = () => {
  const [companyName, setCompanyName] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    message: '',
    isValid: false,
    color: 'gray'
  });
  const navigate = useNavigate();
  
  // Password validation requirements
  const minLength = 8;
  const hasUppercase = /[A-Z]/.test(adminPassword);
  const hasLowercase = /[a-z]/.test(adminPassword);
  const hasNumbers = /\d/.test(adminPassword);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(adminPassword);
  
  // Update password strength when password changes
  useEffect(() => {
    validatePassword(adminPassword);
  }, [adminPassword]);
  
  // Password validation function
  const validatePassword = (password) => {
    // Empty password
    if (!password) {
      setPasswordStrength({
        score: 0,
        message: '',
        isValid: false,
        color: 'gray'
      });
      return;
    }
    
    // Calculate password strength
    let score = 0;
    const checks = [
      password.length >= minLength,
      /[A-Z]/.test(password),
      /[a-z]/.test(password),
      /\d/.test(password),
      /[!@#$%^&*(),.?":{}|<>]/.test(password)
    ];
    
    score = checks.filter(Boolean).length;
    
    // Set password strength message and color
    let message = '';
    let color = '';
    let isValid = false;
    
    // Check if we have the minimum requirements (8 chars, uppercase, lowercase, number)
    const hasMinRequirements = 
      password.length >= minLength && 
      /[A-Z]/.test(password) && 
      /[a-z]/.test(password) && 
      /\d/.test(password);
    
    if (score <= 2) {
      message = 'Weak password';
      color = 'red';
    } else if (score === 3) {
      // Check if the 3 requirements include our minimum set
      if (hasMinRequirements) {
        message = 'Good password';
        color = 'blue';
        isValid = true;
      } else {
        message = 'Fair password';
        color = 'orange';
      }
    } else if (score === 4) {
      message = 'Good password';
      color = 'blue';
      isValid = true;
    } else if (score === 5) {
      message = 'Strong password';
      color = 'green';
      isValid = true;
    }
    
    setPasswordStrength({ score, message, isValid, color });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!companyName || !adminName || !adminEmail || !adminPassword) {
      setError('All fields are required.');
      setLoading(false);
      return;
    }
    
    // Validate password strength before submission
    if (!passwordStrength.isValid) {
      setError('Please use a stronger password. Your password must be at least 8 characters and include uppercase letters, lowercase letters, and numbers. Special characters are recommended for increased security.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post('/api/auth/company-signup', {
        companyName,
        adminName,
        adminEmail,
        adminPassword,
      });

      // Assuming the backend returns a token and user info upon successful signup
      const { token, user, company } = response.data;

      // Store token and user info in localStorage, similar to Login.js
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user)); // User object is directly available in response

      console.log('Company created:', company); // Optional: log company info
      console.log('Stored token and user in localStorage');

      // Redirect to the onboarding flow instead of the main dashboard
      navigate('/onboarding'); 

    } catch (err) {
      console.error('Company Signup Error:', err.response ? err.response.data : err.message);
      setError(err.response?.data?.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="w-full max-w-md p-8 space-y-6 bg-white rounded shadow-md">
        {/* Added heading to match Login page */}
        <h1 className="login-title text-3xl font-bold text-center text-gray-900">Leadbooster CRM</h1> 
        <h2 className="text-2xl font-bold text-center text-gray-900">Create Your Company Account</h2>
        <form className="space-y-6" onSubmit={handleSubmit}>
          {error && <p className="text-sm text-center text-red-600">{error}</p>}
          <div>
            <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
              Company Name
            </label>
            <input
              id="companyName"
              name="companyName"
              type="text"
              required
              className="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Your Company Inc."
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              disabled={loading}
            />
          </div>
          <div>
            <label htmlFor="adminName" className="block text-sm font-medium text-gray-700">
              Your Full Name (Admin)
            </label>
            <input
              id="adminName"
              name="adminName"
              type="text"
              required
              className="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Jane Doe"
              value={adminName}
              onChange={(e) => setAdminName(e.target.value)}
              disabled={loading}
            />
          </div>
          <div>
            <label htmlFor="adminEmail" className="block text-sm font-medium text-gray-700">
              Your Email (Admin)
            </label>
            <input
              id="adminEmail"
              name="adminEmail"
              type="email"
              autoComplete="email"
              required
              className="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="admin@yourcompany.com"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              disabled={loading}
            />
          </div>
          <div>
            <label htmlFor="adminPassword" className="block text-sm font-medium text-gray-700">
              Password (Admin)
            </label>
            <input
              id="adminPassword"
              name="adminPassword"
              type="password"
              autoComplete="new-password"
              required
              className={`w-full px-3 py-2 mt-1 border ${passwordStrength.message ? `border-${passwordStrength.color}-500` : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
              placeholder="••••••••"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              disabled={loading}
            />
            {adminPassword && (
              <div className="mt-2">
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-sm font-medium text-${passwordStrength.color}-500`}>
                    {passwordStrength.message}
                  </span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full bg-${passwordStrength.color}-500`} 
                    style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                  />
                </div>
                <ul className="mt-2 text-xs text-gray-600 space-y-1">
                  <li className={`flex items-center ${adminPassword.length >= minLength ? 'text-green-500' : ''}`}>
                    <span>{adminPassword.length >= minLength ? '✓' : '○'}</span>
                    <span className="ml-1">Minimum 8 characters</span>
                  </li>
                  <li className={`flex items-center ${hasUppercase ? 'text-green-500' : ''}`}>
                    <span>{hasUppercase ? '✓' : '○'}</span>
                    <span className="ml-1">At least one uppercase letter</span>
                  </li>
                  <li className={`flex items-center ${hasLowercase ? 'text-green-500' : ''}`}>
                    <span>{hasLowercase ? '✓' : '○'}</span>
                    <span className="ml-1">At least one lowercase letter</span>
                  </li>
                  <li className={`flex items-center ${hasNumbers ? 'text-green-500' : ''}`}>
                    <span>{hasNumbers ? '✓' : '○'}</span>
                    <span className="ml-1">At least one number</span>
                  </li>
                  <li className={`flex items-center ${hasSpecialChar ? 'text-green-500' : ''}`}>
                    <span>{hasSpecialChar ? '✓' : '○'}</span>
                    <span className="ml-1">At least one special character (recommended)</span>
                  </li>
                </ul>
              </div>
            )}
          </div>
          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? 'Creating Account...' : 'Create Company Account'}
            </button>
          </div>
        </form>
         <p className="mt-4 text-sm text-center text-gray-600">
            Already have an account?{' '}
            <a href="/login" className="font-medium text-indigo-600 hover:text-indigo-500">
              Log in
            </a>
          </p>
      </div>
    </div>
  );
};

export default CompanySignup;
